import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { Bars3Icon, XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import apiClient from '../api/axiosConfig';

const Layout: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { reportProgress } = useWebSocket();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [reportAvailable, setReportAvailable] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Check if a report is available
  useEffect(() => {
    const checkReportAvailability = async () => {
      try {
        const response = await apiClient.get('/api/reports/available');
        setReportAvailable(response.data.available);
        setIsGenerating(response.data.status === 'started' || response.data.status === 'in_progress');
      } catch (error) {
        console.error('Failed to check report availability:', error);
      }
    };

    checkReportAvailability();
  }, []);

  // Update based on WebSocket progress
  useEffect(() => {
    if (reportProgress) {
      setIsGenerating(reportProgress.status === 'started' || reportProgress.status === 'in_progress');
      if (reportProgress.status === 'completed') {
        setReportAvailable(true);
      }
    }
  }, [reportProgress]);

  // Handle report download
  const handleDownloadReport = async () => {
    try {
      // Use apiClient to make the request with authorization headers
      const response = await apiClient.get('/api/reports/download', {
        responseType: 'blob', // Important for handling file downloads
      });

      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));

      // Create a link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'annual-report.xlsx'); // Changed to xlsx as per the backend content type
      document.body.appendChild(link);
      link.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download report:', error);
      // You might want to add user notification here
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Report', href: '/report' },
    { name: 'Excluded Entities', href: '/excluded-entities' },
    { name: 'Mappings', href: '/mappings' },
    { name: 'Bulk User Update', href: '/bulk-user-update' },
  ];

  return (
    <div className="min-h-screen bg-offWhite">
      {/* Navigation */}
      <nav className="bg-boundlessBlue">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <img
                  className="h-8 w-auto"
                  src="/ja-logo.png"
                  alt="JA Worldwide"
                />
              </div>
              <div className="hidden md:block">
                <div className="ml-10 flex items-baseline space-x-4">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${
                        location.pathname === item.href
                          ? 'bg-immersiveBlueBlack text-optimisticIce'
                          : 'text-optimisticIce hover:bg-resilientTurquoise hover:text-immersiveBlueBlack'
                      } rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="ml-4 flex items-center md:ml-6 space-x-4">
                {reportAvailable ? (
                  <button
                    type="button"
                    onClick={handleDownloadReport}
                    className="rounded-md bg-resilientTurquoise px-3 py-2 text-sm font-medium text-immersiveBlueBlack hover:bg-optimisticIce focus:outline-none focus:ring-2 focus:ring-optimisticIce focus:ring-offset-2 focus:ring-offset-boundlessBlue transition-colors duration-200"
                  >
                    <div className="flex items-center">
                      <ArrowDownTrayIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
                      Download Report
                    </div>
                  </button>
                ) : isGenerating ? (
                  <div className="rounded-md bg-gray-300 px-3 py-2 text-sm font-medium text-gray-700">
                    Generating Report...
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate('/report')}
                    className="rounded-md bg-resilientTurquoise px-3 py-2 text-sm font-medium text-immersiveBlueBlack hover:bg-optimisticIce focus:outline-none focus:ring-2 focus:ring-optimisticIce focus:ring-offset-2 focus:ring-offset-boundlessBlue transition-colors duration-200"
                  >
                    Generate Report
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="rounded-md bg-empoweredYellow px-3 py-2 text-sm font-medium text-immersiveBlueBlack hover:bg-grittyGreen focus:outline-none focus:ring-2 focus:ring-optimisticIce focus:ring-offset-2 focus:ring-offset-boundlessBlue transition-colors duration-200"
                >
                  Logout
                </button>
              </div>
            </div>
            <div className="-mr-2 flex md:hidden">
              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                type="button"
                className="inline-flex items-center justify-center rounded-md bg-resilientTurquoise p-2 text-immersiveBlueBlack hover:bg-optimisticIce focus:outline-none focus:ring-2 focus:ring-optimisticIce focus:ring-offset-2 focus:ring-offset-boundlessBlue transition-colors duration-200"
              >
                <span className="sr-only">Open main menu</span>
                {isMobileMenuOpen ? (
                  <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu, show/hide based on menu state */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="space-y-1 px-2 pb-3 pt-2 sm:px-3">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${
                    location.pathname === item.href
                      ? 'bg-immersiveBlueBlack text-optimisticIce'
                      : 'text-optimisticIce hover:bg-resilientTurquoise hover:text-immersiveBlueBlack'
                  } block rounded-md px-3 py-2 text-base font-medium transition-colors duration-200`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
            <div className="border-t border-resilientTurquoise pb-3 pt-4">
              <div className="flex flex-col space-y-2 px-5">
                {reportAvailable ? (
                  <button
                    type="button"
                    onClick={handleDownloadReport}
                    className="flex items-center rounded-md bg-resilientTurquoise px-3 py-2 text-base font-medium text-immersiveBlueBlack hover:bg-optimisticIce transition-colors duration-200"
                  >
                    <ArrowDownTrayIcon className="-ml-0.5 mr-2 h-5 w-5" aria-hidden="true" />
                    Download Report
                  </button>
                ) : isGenerating ? (
                  <div className="rounded-md bg-gray-300 px-3 py-2 text-base font-medium text-gray-700">
                    Generating Report...
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/report');
                      setIsMobileMenuOpen(false);
                    }}
                    className="rounded-md bg-resilientTurquoise px-3 py-2 text-base font-medium text-immersiveBlueBlack hover:bg-optimisticIce transition-colors duration-200"
                  >
                    Generate Report
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="rounded-md bg-empoweredYellow px-3 py-2 text-base font-medium text-immersiveBlueBlack hover:bg-grittyGreen transition-colors duration-200"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main content */}
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-optimisticIce">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center">
            <p className="text-center text-sm text-immersiveBlueBlack font-medium">
              &copy; {new Date().getFullYear()} JA Worldwide. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
